import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, Pagination, Alert, Tabs, Tab, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Visibility as VisibilityIcon,
  Close as CloseIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import { filterBySubjectScope } from 'modules/platform-components/utils/subject-scope';
import {
  apiPaths, getInstutueApp,
} from 'modules/platform-components/api-integration-util';

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

const STYLE_TAG_LABELS = {
  CLINICAL_VIGNETTE: 'Clinical Scenario',
  CROSS_SUBJECT: 'Cross-Subject',
  NEXT_BEST_STEP: 'Next Best Step',
  EXCEPT_NOT: 'Except/NOT',
  ONE_LINER: 'One-Liner',
  DRUG_IOC: 'Drug/IOC',
  IMAGE_DESCRIPTION: 'Image-Based',
  MATCH_FOLLOWING: 'Match the Following',
};

const statusConfig = {
  pending: { label: 'Pending', color: '#f59e0b' },
  applied: { label: 'Applied', color: '#22c55e' },
  inserted: { label: 'Inserted', color: '#22c55e' },
  rejected: { label: 'Rejected', color: '#dc3545' },
};

// ─── Stats Dashboard ────────────────────────────────────────────────
function StatsDashboard({ stats, onCardClick, activeFilter }) {
  if (!stats) return null;
  const appliedLabel = stats.applied !== undefined ? 'Applied' : 'Inserted';
  const appliedKey = stats.applied !== undefined ? 'applied' : 'inserted';
  const cards = [
    { label: 'Pending', value: stats.pending ?? 0, color: '#f59e0b', bg: '#fffbeb', filterKey: 'pending' },
    { label: appliedLabel,
      value: stats.applied ?? stats.inserted ?? 0, color: '#22c55e', bg: '#f0fdf4', filterKey: appliedKey },
    { label: 'Rejected', value: stats.rejected ?? 0, color: '#dc3545', bg: '#fef2f2', filterKey: 'rejected' },
    { label: 'Total', value: stats.total ?? 0, color: '#3366FF', bg: '#eef2ff', filterKey: 'all' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      {cards.map(card => {
        const isActive = activeFilter === card.filterKey;
        return (
          <Paper key={card.label} elevation={1}
            onClick={() => onCardClick && card.value > 0 && onCardClick(card.filterKey)}
            sx={{
              p: 2, minWidth: 130, textAlign: 'center', backgroundColor: card.bg,
              borderLeft: `4px solid ${card.color}`,
              cursor: onCardClick && card.value > 0 ? 'pointer' : 'default',
              outline: isActive ? `2px solid ${card.color}` : 'none',
              outlineOffset: -2,
              transition: 'all 0.2s ease',
              '&:hover': onCardClick && card.value > 0 ? { boxShadow: 4, transform: 'translateY(-2px)' } : {},
            }}>
            <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold' }}>{card.value}</Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>{card.label}</Typography>
          </Paper>
        );
      })}
    </Box>
  );
}

// ─── Variation Detail Dialog ────────────────────────────────────────
function VariationDetailDialog({ open, onClose, variation, mode }) {
  if (!open || !variation) return null;

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {mode === 'restyle' ? 'Restyled' : 'Generated'} Variation #{variation.id}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>ORIGINAL QUESTION</Typography>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
            <div dangerouslySetInnerHTML={{ __html: variation.originalQuestion || 'N/A' }} />
          </Paper>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
            {mode === 'restyle' ? 'RESTYLED QUESTION' : 'GENERATED QUESTION'}
          </Typography>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <div dangerouslySetInnerHTML={{
              __html: mode === 'restyle' ? variation.restyledQuestion : variation.generatedQuestion
            }} />
          </Paper>
        </Box>
        {mode === 'generate' && variation.generatedOptions && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>OPTIONS</Typography>
            {(() => {
              try {
                const opts = typeof variation.generatedOptions === 'string'
                  ? JSON.parse(variation.generatedOptions) : variation.generatedOptions;
                return opts.map((opt, i) => (
                  <Typography key={i} variant="body2" sx={{
                    fontWeight: opt.correct ? 'bold' : 'normal',
                    color: opt.correct ? '#22c55e' : 'inherit', ml: 1,
                  }}>
                    {String.fromCharCode(65 + i)}) {opt.answer} {opt.correct && '✓'}
                  </Typography>
                ));
              } catch { return null; }
            })()}
          </Box>
        )}
        <Box>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>EXPLANATION</Typography>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}>
            <div dangerouslySetInnerHTML={{
              __html: (mode === 'restyle' ? variation.restyledExplanation : variation.generatedExplanation) || 'N/A'
            }} />
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Question Card (grouped variations) ─────────────────────────────
function QuestionCard({ questionId, variations, mode, onViewVariation }) {
  const [expanded, setExpanded] = useState(false);

  const pendingCount = variations.filter(v => v.status === 'pending').length;
  const appliedCount = variations.filter(v => v.status === 'applied' || v.status === 'inserted').length;
  const rejectedCount = variations.filter(v => v.status === 'rejected').length;
  const totalCount = variations.length;
  const hasPending = pendingCount > 0;

  // Get unique style tags for this question
  const styleTags = [...new Set(variations.map(v => v.styleTag).filter(Boolean))];

  // Get the original question text from the first variation that has it
  const originalQuestion = variations.find(v => v.originalQuestion)?.originalQuestion;
  // Strip HTML for preview
  const plainText = originalQuestion
    ? originalQuestion.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    : '';
  const preview = plainText.length > 120 ? plainText.substring(0, 120) + '...' : plainText;

  return (
    <Paper variant="outlined" sx={{
      mb: 1.5,
      borderLeft: hasPending ? '4px solid #f59e0b' : '4px solid #22c55e',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 2 },
    }}>
      {/* Card header — always visible */}
      <Box
        sx={{ p: 2, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        onClick={() => setExpanded(e => !e)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Question #{questionId}
            </Typography>
            <Chip label={`${totalCount} variation${totalCount > 1 ? 's' : ''}`} size="small"
              sx={{ fontSize: 10, height: 20 }} />
            {styleTags.map(tag => (
              <Chip key={tag} label={STYLE_TAG_LABELS[tag] || tag} size="small"
                sx={{ fontSize: 10, height: 20, backgroundColor: '#e0e7ff', color: '#3730a3' }} />
            ))}
          </Box>
          {preview && (
            <Typography variant="caption" color="textSecondary" sx={{
              display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {preview}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
            {pendingCount > 0 && (
              <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                {pendingCount} pending
              </Typography>
            )}
            {appliedCount > 0 && (
              <Typography variant="caption" sx={{ color: '#22c55e' }}>
                {appliedCount} {mode === 'restyle' ? 'applied' : 'inserted'}
              </Typography>
            )}
            {rejectedCount > 0 && (
              <Typography variant="caption" sx={{ color: '#dc3545' }}>
                {rejectedCount} rejected
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev); }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expanded content — variation rows */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell sx={{ fontSize: 11, fontWeight: 'bold' }}>ID</TableCell>
                  {mode === 'restyle' && <TableCell sx={{ fontSize: 11, fontWeight: 'bold' }}>Style</TableCell>}
                  <TableCell sx={{ fontSize: 11, fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variations.map(v => {
                  const st = statusConfig[v.status] || { label: v.status, color: '#666' };
                  return (
                    <TableRow key={v.id} hover>
                      <TableCell sx={{ fontSize: 12 }}>{v.id}</TableCell>
                      {mode === 'restyle' && (
                        <TableCell>
                          {v.styleTag ? (
                            <Chip label={STYLE_TAG_LABELS[v.styleTag] || v.styleTag} size="small"
                              sx={{ fontSize: 10, height: 20 }} />
                          ) : '—'}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip label={st.label} size="small" variant="outlined"
                          sx={{ borderColor: st.color, color: st.color, fontSize: 10, height: 20 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => onViewVariation(v)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function QuestionVariations() {
  const [activeTab, setActiveTab] = useState(0);
  const [restyleStats, setRestyleStats] = useState(null);
  const [generateStats, setGenerateStats] = useState(null);
  const [dialogVariation, setDialogVariation] = useState(null);

  // List state
  const [variations, setVariations] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reviewedByFilter, setReviewedByFilter] = useState('');
  const [activeStatsFilter, setActiveStatsFilter] = useState(null);
  const pageSize = 20;

  const fetchStats = useCallback(async () => {
    const headers = getHeaders();
    try {
      const [rs, gs] = await Promise.all([
        getInstutueApp(apiPaths.questionVariationRestyleStats, null, headers),
        getInstutueApp(apiPaths.questionVariationGenerateStats, null, headers),
      ]);
      if (rs?.success) setRestyleStats(rs);
      if (gs?.success) setGenerateStats(gs);
    } catch (e) {
      console.error('Failed to fetch stats', e);
    }
  }, []);

  const fetchVariations = useCallback(async () => {
    setListLoading(true);
    try {
      const params = { page: page - 1, pageSize };
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.fromDate = dateFrom;
      if (dateTo) params.toDate = dateTo;
      if (reviewedByFilter) params.reviewedBy = reviewedByFilter;

      const endpoint = activeTab === 0
        ? apiPaths.questionVariationRestyleList
        : apiPaths.questionVariationGenerateList;

      const data = await getInstutueApp(endpoint, params, getHeaders());
      if (data?.success) {
        setVariations(
          filterBySubjectScope(data.variations || [], (v) => v.subjectId ?? v.subject_id)
        );
        setTotalPages(data.totalPages || 1);
      } else {
        setVariations([]);
        setTotalPages(1);
      }
    } catch (e) {
      console.error('Failed to fetch variations', e);
      setVariations([]);
    } finally {
      setListLoading(false);
    }
  }, [page, statusFilter, activeTab, dateFrom, dateTo, reviewedByFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchVariations(); }, [fetchVariations]);

  const handleTabChange = (e, v) => {
    setActiveTab(v);
    setPage(1);
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setReviewedByFilter('');
    setActiveStatsFilter(null);
  };

  const handleStatsCardClick = (filterKey) => {
    if (activeStatsFilter === filterKey) {
      setActiveStatsFilter(null);
      setStatusFilter('');
      setPage(1);
      return;
    }
    setActiveStatsFilter(filterKey);
    setPage(1);
    if (filterKey === 'all') {
      setStatusFilter('');
    } else {
      setStatusFilter(filterKey);
    }
  };

  const handleStatusDropdownChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
    setActiveStatsFilter(null);
  };

  const currentStats = activeTab === 0 ? restyleStats : generateStats;
  const mode = activeTab === 0 ? 'restyle' : 'generate';
  const filteredStatusConfig = Object.fromEntries(
    Object.entries(statusConfig).filter(([key]) =>
      activeTab === 0 ? key !== 'inserted' : key !== 'applied'
    )
  );

  // Group variations by questionId
  const groupedVariations = variations.reduce((acc, v) => {
    const qId = v.questionId || v.sourceQuestionId;
    if (!acc[qId]) acc[qId] = [];
    acc[qId].push(v);
    return acc;
  }, {});

  // Sort groups: questions with pending variations first, then by questionId desc
  const sortedGroups = Object.entries(groupedVariations).sort(([aId, aVars], [bId, bVars]) => {
    const aHasPending = aVars.some(v => v.status === 'pending');
    const bHasPending = bVars.some(v => v.status === 'pending');
    if (aHasPending && !bHasPending) return -1;
    if (!aHasPending && bHasPending) return 1;
    return Number(bId) - Number(aId);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Question Variations</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { fetchStats(); fetchVariations(); }} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Restyled Questions" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
        <Tab label="Generated Questions" sx={{ textTransform: 'none', fontWeight: 'bold' }} />
      </Tabs>

      <StatsDashboard stats={currentStats} onCardClick={handleStatsCardClick} activeFilter={activeStatsFilter} />

      {/* Status filter dropdown */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={handleStatusDropdownChange}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(filteredStatusConfig).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {statusFilter && (
          <Chip
            label={`Filtered: ${statusConfig[statusFilter]?.label || statusFilter}`}
            onDelete={() => { setStatusFilter(''); setActiveStatsFilter(null); setPage(1); }}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        <TextField
          size="small" type="date" label="From Date" value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" type="date" label="To Date" value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" label="Reviewed By" value={reviewedByFilter}
          onChange={(e) => { setReviewedByFilter(e.target.value); setPage(1); }}
          placeholder="Email or name" sx={{ minWidth: 160 }}
        />
        {(dateFrom || dateTo || reviewedByFilter) && (
          <Chip
            label="Clear date/user filters"
            onDelete={() => { setDateFrom(''); setDateTo(''); setReviewedByFilter(''); setPage(1); }}
            size="small" color="primary" variant="outlined"
          />
        )}
        <Typography variant="caption" color="textSecondary">
          {sortedGroups.length} question{sortedGroups.length !== 1 ? 's' : ''} on this page
        </Typography>
      </Box>

      {/* Grouped question cards */}
      {listLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : variations.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {statusFilter
            ? `No ${mode === 'restyle' ? 'restyled' : 'generated'} variations found with status "${statusConfig[statusFilter]?.label || statusFilter}".`
            : `No ${mode === 'restyle' ? 'restyled' : 'generated'} variations found yet. Use the "AI Variations" button on any question in the Question Viewer to ${mode === 'restyle' ? 'restyle' : 'generate'} variations.`
          }
        </Alert>
      ) : (
        <>
          {sortedGroups.map(([qId, vars]) => (
            <QuestionCard
              key={qId}
              questionId={qId}
              variations={vars}
              mode={mode}
              onViewVariation={setDialogVariation}
            />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </>
      )}

      <VariationDetailDialog
        open={!!dialogVariation}
        onClose={() => setDialogVariation(null)}
        variation={dialogVariation}
        mode={mode}
      />
    </Box>
  );
}
